import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext';
import CampaignDetailModal from './CampaignDetailModal';
import { apiRequest, getApiUrl } from '../utils/api';

const CampaignsList = ({ campaigns, onCampaignDelete }) => {
  const { isAdmin, user } = useAuth();
  const INITIAL_VISIBLE = 25;
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [selectedCaption, setSelectedCaption] = useState('');
  const [editingShares, setEditingShares] = useState(null);
  const [shareEditorField, setShareEditorField] = useState('shares');
  const [sharesValue, setSharesValue] = useState('');
  const [editingAmount, setEditingAmount] = useState(null);
  const [amountValue, setAmountValue] = useState('');
  const [editingViews, setEditingViews] = useState(null);
  const [viewsValue, setViewsValue] = useState('');
  const [editingLikes, setEditingLikes] = useState(null);
  const [likesValue, setLikesValue] = useState('');
  const [editingComments, setEditingComments] = useState(null);
  const [commentsValue, setCommentsValue] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');
  const [refreshingCampaigns, setRefreshingCampaigns] = useState(new Set());
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const formatPercentage = (num) => {
    // num is already a percentage (0-100). Do not multiply again.
    const val = Number.isFinite(num) ? num : 0;
    return val.toFixed(2) + '%';
  };

  const handleCampaignExcelExport = async () => {
    try {
      if (!user?.token) {
        alert('Please log in again to export.');
        return;
      }

      const params = new URLSearchParams();
      params.append('module', 'campaigns');
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await apiRequest(`/reports/excel?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });

      if (!response.ok) {
        let message = `Export failed (${response.status})`;
        try {
          const err = await response.json();
          message = err.detail || err.error || err.message || message;
        } catch (_) {}
        alert(message);
        return;
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition');
      let filename = 'campaigns-report.xlsx';
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.message || 'Failed to export campaigns.');
    }
  };

  const getVerdictColor = (verdict) => {
    if (!verdict || typeof verdict !== 'string') {
      return 'bg-gray-100 text-gray-800';
    }
    
    if (verdict.includes('Excellent')) return 'bg-green-100 text-green-800';
    if (verdict.includes('Good')) return 'bg-blue-100 text-blue-800';
    if (verdict.includes('Okay')) return 'bg-yellow-100 text-yellow-800';
    if (verdict.includes('Poor')) return 'bg-red-100 text-red-800';
    if (verdict.includes('No Cost')) return 'bg-purple-100 text-purple-800';
    if (verdict.includes('No Data')) return 'bg-gray-100 text-gray-800';
    return 'bg-gray-100 text-gray-800';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDelete = async (campaignId, influencerName) => {
    if (!window.confirm(`Are you sure you want to delete the campaign for "${influencerName}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingId(campaignId);
    try {
      const response = await apiRequest(`/campaigns/${campaignId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        alert('Campaign deleted successfully!');
        if (onCampaignDelete) {
          onCampaignDelete();
        }
      } else {
        alert('Error deleting campaign: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Network error: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCaptionClick = (caption) => {
    setSelectedCaption(caption);
    setShowCaptionModal(true);
  };

  const handlePostLinkClick = (postLink) => {
    // Open Instagram post in new tab
    window.open(postLink, '_blank');
  };

  const handleVideoClick = (videoUrl) => {
    setSelectedVideoUrl(videoUrl);
    setShowVideoModal(true);
  };

  const handleRefreshMetrics = async (campaignId) => {
    try {
      setRefreshingCampaigns(prev => new Set(prev).add(campaignId));
      
      const response = await fetch(`${getApiUrl()}/campaigns/${campaignId}/refresh-metrics`, {
        method: 'POST',
        headers: {
          'Authorization': user?.token ? `Bearer ${user.token}` : '',
          'Content-Type': 'application/json',
        },
      });

      // Check if response is OK before parsing JSON
      if (!response.ok) {
        let errorMessage = 'Unknown error';
        try {
          const errorData = await response.json();
          // FastAPI returns errors in 'detail' field
          errorMessage = errorData.detail || errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = `HTTP ${response.status}: ${response.statusText || 'Unknown error'}`;
        }
        alert('Error refreshing metrics: ' + errorMessage);
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        alert('Campaign metrics refreshed successfully!');
        // Trigger parent refresh to update the campaign data
        if (onCampaignDelete) {
          onCampaignDelete();
        }
      } else {
        // Handle case where response is OK but success is false
        const errorMessage = data.detail || data.error || data.message || 'Unknown error';
        alert('Error refreshing metrics: ' + errorMessage);
      }
    } catch (err) {
      console.error('Error refreshing metrics:', err);
      alert('Network error: ' + (err.message || 'Failed to refresh metrics. Please check your connection and try again.'));
    } finally {
      setRefreshingCampaigns(prev => {
        const newSet = new Set(prev);
        newSet.delete(campaignId);
        return newSet;
      });
    }
  };

  const handleVideoModalClose = () => {
    setShowVideoModal(false);
    setSelectedVideoUrl('');
  };

  const handleSharesEdit = (campaignId, currentValue, field = 'shares') => {
    setEditingShares(campaignId);
    setShareEditorField(field);
    setSharesValue((currentValue ?? 0).toString());
  };

  const handleSharesSave = async (campaignId, field = 'shares') => {
    try {
      const isAdminUser = isAdmin();
      const numericValue = parseInt(sharesValue, 10) || 0;
      const isDirectShareUpdate = isAdminUser && field === 'shares';
      const endpoint = isDirectShareUpdate
        ? `/campaigns/${campaignId}/shares`
        : `/campaigns/${campaignId}/change-requests`;
      const payload = isDirectShareUpdate
        ? { shares: numericValue }
        : { field: field === 'shares' ? 'shares' : 'reposts', value: numericValue };
      const response = await apiRequest(endpoint, {
        method: isDirectShareUpdate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        // If response is not JSON, use status text
        alert('Error updating shares: ' + response.statusText + ' (HTTP ' + response.status + ')');
        return;
      }

      if (!response.ok) {
        // Handle HTTP error responses (FastAPI returns 'detail' field)
        let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        if (data) {
          if (data.detail) {
            errorMsg = typeof data.detail === 'string' ? data.detail : (Array.isArray(data.detail) ? data.detail.join(', ') : JSON.stringify(data.detail));
          } else if (data.error) {
            errorMsg = typeof data.error === 'string' ? data.error : (Array.isArray(data.error) ? data.error.join(', ') : JSON.stringify(data.error));
          } else if (data.message) {
            errorMsg = typeof data.message === 'string' ? data.message : (Array.isArray(data.message) ? data.message.join(', ') : JSON.stringify(data.message));
          }
        }
        alert('Error updating shares: ' + errorMsg);
        return;
      }

      if (data.success) {
        if (isDirectShareUpdate) {
          alert('Shares updated successfully!');
        } else {
          alert(`${field === 'shares' ? 'Shares' : 'Reshares'} change request submitted for approval.`);
        }
        // Refresh the campaigns list
        if (onCampaignDelete) {
          onCampaignDelete(); // This will trigger a refresh
        }
      } else {
        alert('Error updating shares: ' + (data.error || data.detail || 'Unknown error'));
      }
    } catch (err) {
      alert('Network error: ' + err.message);
    } finally {
      setEditingShares(null);
      setShareEditorField('shares');
      setSharesValue('');
    }
  };

  const handleSharesCancel = () => {
    setEditingShares(null);
    setShareEditorField('shares');
    setSharesValue('');
  };

  // Amount edit functions
  const handleAmountEdit = (campaignId, currentAmount) => {
    setEditingAmount(campaignId);
    setAmountValue(currentAmount.toString());
  };

  const handleAmountSave = async (campaignId) => {
    try {
      const isAdminUser = isAdmin();
      const endpoint = isAdminUser
        ? `/campaigns/${campaignId}/amount`
        : `/campaigns/${campaignId}/change-requests`;
      const payload = isAdminUser
        ? { amount: parseFloat(amountValue) || 0 }
        : { field: 'amount', value: parseFloat(amountValue) || 0 };
      const response = await apiRequest(endpoint, {
        method: isAdminUser ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        // If response is not JSON, use status text
        alert('Error updating amount: ' + response.statusText + ' (HTTP ' + response.status + ')');
        return;
      }

      // Log for debugging
      console.log('Response status:', response.status, 'Response data:', data);

      if (!response.ok) {
        // Handle HTTP error responses
        let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        if (data) {
          // FastAPI validation errors can be in detail array
          if (data.detail) {
            if (Array.isArray(data.detail)) {
              errorMsg = data.detail.map(err => {
                if (typeof err === 'object' && err.msg) return err.msg;
                return String(err);
              }).join(', ');
            } else if (typeof data.detail === 'string') {
              errorMsg = data.detail;
            } else {
              errorMsg = JSON.stringify(data.detail);
            }
          } else if (data.error) {
            errorMsg = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
          } else if (data.message) {
            errorMsg = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
          }
        }
        alert('Error updating amount: ' + errorMsg);
        return;
      }

      if (data.success) {
        alert(isAdminUser ? 'Amount updated successfully!' : 'Change request submitted for approval.');
        if (onCampaignDelete) {
          onCampaignDelete();
        }
      } else {
        const errorMsg = data.error || data.detail || 'Unknown error';
        alert('Error updating amount: ' + (typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg)));
      }
    } catch (err) {
      console.error('Error in handleAmountSave:', err);
      alert('Network error: ' + err.message);
    } finally {
      setEditingAmount(null);
      setAmountValue('');
    }
  };

  const handleAmountCancel = () => {
    setEditingAmount(null);
    setAmountValue('');
  };

  // Views edit functions
  const handleViewsEdit = (campaignId, currentViews) => {
    setEditingViews(campaignId);
    setViewsValue(currentViews.toString());
  };

  const handleViewsSave = async (campaignId) => {
    try {
      const isAdminUser = isAdmin();
      const endpoint = isAdminUser
        ? `/campaigns/${campaignId}/views`
        : `/campaigns/${campaignId}/change-requests`;
      const payload = isAdminUser
        ? { views: parseInt(viewsValue) || 0 }
        : { field: 'views', value: parseInt(viewsValue) || 0 };
      const response = await apiRequest(endpoint, {
        method: isAdminUser ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        // If response is not JSON, use status text
        alert('Error updating views: ' + response.statusText + ' (HTTP ' + response.status + ')');
        return;
      }

      if (!response.ok) {
        // Handle HTTP error responses (FastAPI returns 'detail' field)
        let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        if (data) {
          if (data.detail) {
            errorMsg = typeof data.detail === 'string' ? data.detail : (Array.isArray(data.detail) ? data.detail.join(', ') : JSON.stringify(data.detail));
          } else if (data.error) {
            errorMsg = typeof data.error === 'string' ? data.error : (Array.isArray(data.error) ? data.error.join(', ') : JSON.stringify(data.error));
          } else if (data.message) {
            errorMsg = typeof data.message === 'string' ? data.message : (Array.isArray(data.message) ? data.message.join(', ') : JSON.stringify(data.message));
          }
        }
        alert('Error updating views: ' + errorMsg);
        return;
      }

      if (data.success) {
        alert(isAdminUser ? 'Views updated successfully!' : 'Change request submitted for approval.');
        if (onCampaignDelete) {
          onCampaignDelete();
        }
      } else {
        alert('Error updating views: ' + (data.error || data.detail || 'Unknown error'));
      }
    } catch (err) {
      alert('Network error: ' + err.message);
    } finally {
      setEditingViews(null);
      setViewsValue('');
    }
  };

  const handleViewsCancel = () => {
    setEditingViews(null);
    setViewsValue('');
  };

  // Likes edit functions
  const handleLikesEdit = (campaignId, currentLikes) => {
    setEditingLikes(campaignId);
    setLikesValue(currentLikes.toString());
  };

  const handleLikesSave = async (campaignId) => {
    try {
      const isAdminUser = isAdmin();
      const endpoint = isAdminUser
        ? `/campaigns/${campaignId}/likes`
        : `/campaigns/${campaignId}/change-requests`;
      const payload = isAdminUser
        ? { likes: parseInt(likesValue) || 0 }
        : { field: 'likes', value: parseInt(likesValue) || 0 };
      const response = await apiRequest(endpoint, {
        method: isAdminUser ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        // If response is not JSON, use status text
        alert('Error updating likes: ' + response.statusText + ' (HTTP ' + response.status + ')');
        return;
      }

      if (!response.ok) {
        // Handle HTTP error responses (FastAPI returns 'detail' field)
        let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        if (data) {
          if (data.detail) {
            errorMsg = typeof data.detail === 'string' ? data.detail : (Array.isArray(data.detail) ? data.detail.join(', ') : JSON.stringify(data.detail));
          } else if (data.error) {
            errorMsg = typeof data.error === 'string' ? data.error : (Array.isArray(data.error) ? data.error.join(', ') : JSON.stringify(data.error));
          } else if (data.message) {
            errorMsg = typeof data.message === 'string' ? data.message : (Array.isArray(data.message) ? data.message.join(', ') : JSON.stringify(data.message));
          }
        }
        alert('Error updating likes: ' + errorMsg);
        return;
      }

      if (data.success) {
        alert(isAdminUser ? 'Likes updated successfully!' : 'Change request submitted for approval.');
        if (onCampaignDelete) {
          onCampaignDelete();
        }
      } else {
        alert('Error updating likes: ' + (data.error || data.detail || 'Unknown error'));
      }
    } catch (err) {
      alert('Network error: ' + err.message);
    } finally {
      setEditingLikes(null);
      setLikesValue('');
    }
  };

  const handleLikesCancel = () => {
    setEditingLikes(null);
    setLikesValue('');
  };

  // Comments edit functions
  const handleCommentsEdit = (campaignId, currentComments) => {
    setEditingComments(campaignId);
    setCommentsValue(currentComments.toString());
  };

  const handleCommentsSave = async (campaignId) => {
    try {
      const isAdminUser = isAdmin();
      const endpoint = isAdminUser
        ? `/campaigns/${campaignId}/comments`
        : `/campaigns/${campaignId}/change-requests`;
      const payload = isAdminUser
        ? { comments: parseInt(commentsValue) || 0 }
        : { field: 'comments', value: parseInt(commentsValue) || 0 };
      const response = await apiRequest(endpoint, {
        method: isAdminUser ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        // If response is not JSON, use status text
        alert('Error updating comments: ' + response.statusText + ' (HTTP ' + response.status + ')');
        return;
      }

      if (!response.ok) {
        // Handle HTTP error responses (FastAPI returns 'detail' field)
        let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        if (data) {
          if (data.detail) {
            errorMsg = typeof data.detail === 'string' ? data.detail : (Array.isArray(data.detail) ? data.detail.join(', ') : JSON.stringify(data.detail));
          } else if (data.error) {
            errorMsg = typeof data.error === 'string' ? data.error : (Array.isArray(data.error) ? data.error.join(', ') : JSON.stringify(data.error));
          } else if (data.message) {
            errorMsg = typeof data.message === 'string' ? data.message : (Array.isArray(data.message) ? data.message.join(', ') : JSON.stringify(data.message));
          }
        }
        alert('Error updating comments: ' + errorMsg);
        return;
      }

      if (data.success) {
        alert(isAdminUser ? 'Comments updated successfully!' : 'Change request submitted for approval.');
        if (onCampaignDelete) {
          onCampaignDelete();
        }
      } else {
        alert('Error updating comments: ' + (data.error || data.detail || 'Unknown error'));
      }
    } catch (err) {
      alert('Network error: ' + err.message);
    } finally {
      setEditingComments(null);
      setCommentsValue('');
    }
  };

  const handleCommentsCancel = () => {
    setEditingComments(null);
    setCommentsValue('');
  };

  const handleShowMore = () => {
    setVisibleCount((prev) => Math.min(prev + INITIAL_VISIBLE, filteredAndSortedCampaigns.length));
  };

  const handleShowLess = () => {
    setVisibleCount(INITIAL_VISIBLE);
  };

  const filteredAndSortedCampaigns = campaigns
    .filter(campaign => {
      // Search filter
      const matchesSearch = campaign.influencer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.platform.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.product.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Date range filter
      const campaignDate = campaign.date || '';
      const matchesDateRange = (!startDate || campaignDate >= startDate) && 
                              (!endDate || campaignDate <= endDate);
      
      return matchesSearch && matchesDateRange;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const visibleCampaigns = filteredAndSortedCampaigns.slice(0, visibleCount);

  const totalSpend = campaigns.reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalViews = campaigns.reduce((sum, c) => sum + (c.views || 0), 0);
  const avgEr = campaigns.length
    ? campaigns.reduce((sum, c) => sum + (c.er_percentage || 0), 0) / campaigns.length
    : 0;

  const headerStats = [
    {
      label: 'Active Campaigns',
      value: filteredAndSortedCampaigns.length,
      subValue: `${campaigns.length} total records`,
      icon: (
        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7H8m8 0l-4-4m4 4l-4 4m-4 6h8m-8 0l4 4m-4-4l4-4" />
        </svg>
      ),
    },
    {
      label: 'Total Spend',
      value: formatCurrency(totalSpend),
      subValue: 'Incl. approved campaigns',
      icon: (
        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
    },
    {
      label: 'Average ER%',
      value: formatPercentage(avgEr || 0),
      subValue: `${formatNumber(totalViews)} total views`,
      icon: (
        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5a7 7 0 017 7 7 7 0 01-7 7M11 5a7 7 0 00-7 7 7 7 0 007 7m0-14V3m0 16v2m4-9h2m-12 0H5" />
        </svg>
      ),
    },
  ];

  if (!campaigns || campaigns.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
          <p className="text-gray-500">Upload a screenshot or add a campaign manually to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-100 bg-gradient-to-br from-white via-blue-50 to-indigo-50 p-6 sm:p-8 shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/70 text-blue-600 shadow-inner shadow-white/70 mb-4">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900">Campaigns</h2>
            <p className="mt-2 text-slate-600 max-w-xl">
              Manage and analyze your influencer initiatives with real-time engagement insights, filtered views, and fast exports.
            </p>
            <div className="mt-4 inline-flex items-center px-3 py-1.5 rounded-full bg-white/70 text-sm text-slate-600 backdrop-blur">
              <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
              {filteredAndSortedCampaigns.length} visible · {campaigns.length} total
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1">
            {headerStats.map((stat) => (
              <div key={stat.label} className="bg-white/80 border border-white/60 rounded-2xl p-4 shadow-sm backdrop-blur">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-slate-500">{stat.label}</p>
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-slate-100">
                    {stat.icon}
                  </span>
                </div>
                <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
                <p className="text-xs text-slate-500 mt-1">{stat.subValue}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute -right-12 -top-12 w-48 h-48 bg-gradient-to-br from-indigo-200/60 to-blue-200/30 rounded-full blur-3xl pointer-events-none"></div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-4 sm:p-6 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="flex-1 w-full">
            <label className="text-xs uppercase tracking-wide text-slate-500 mb-2 block">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by influencer, platform, or product"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              />
            </div>
          </div>
          <button
            onClick={handleCampaignExcelExport}
            className="inline-flex items-center justify-center px-4 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-green-500 shadow-lg shadow-emerald-100 hover:shadow-emerald-200 transition hover:-translate-y-0.5"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2z" />
            </svg>
            Export Excel
          </button>
        </div>

        {/* Date Range Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500 mb-2 block">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500 mb-2 block">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
              className="w-full px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition"
            >
              Clear Dates
            </button>
          </div>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50 compact-head">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Caption
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Short Code
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Post Link
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Views
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Likes
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Comments
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Reshares
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Shares
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TCS
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TCS Verdict
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ER%
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ER Verdict
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CPV
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CPV Verdict
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CPE
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CPE Verdict
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost/Comment
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comment Verdict
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost/Share
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Share Verdict
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost/Repost
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Repost Verdict
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Video URL
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 compact-row">
              {visibleCampaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50 group">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {campaign.date || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <button
                        type="button"
                        onClick={() => { setSelectedCampaign(campaign); setShowDetailModal(true); }}
                        className="text-sm font-medium text-blue-600 hover:underline"
                        title="View influencer campaign details"
                      >
                        {campaign.influencer_name}
                      </button>
                      <div className="text-sm text-gray-500">
                        {campaign.product}
                        {campaign.teamMembers && campaign.teamMembers.length > 0
                          ? ` | ${campaign.teamMembers.join(', ')}`
                          : (campaign.team_member ? ` | ${campaign.team_member}` : '')}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {campaign.caption ? (
                      <button
                        onClick={() => handleCaptionClick(campaign.caption)}
                        className="inline-flex items-center justify-center w-8 h-8 bg-green-100 hover:bg-green-200 text-green-600 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        title={`View caption: ${campaign.caption.substring(0, 50)}...`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                        </svg>
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">No caption</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {campaign.short_code || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {campaign.post_link ? (
                      <button
                        onClick={() => handlePostLinkClick(campaign.post_link)}
                        className="inline-flex items-center justify-center w-8 h-8 bg-pink-100 hover:bg-pink-200 text-pink-600 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                        title={`Open Instagram post`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs" title="Post link not available for older campaigns">
                        No link
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {campaign.platform}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900 tabular-nums w-28">
                    {editingAmount === campaign.id ? (
                      <div className="flex flex-col space-y-2">
                        <input
                          type="number"
                          value={amountValue}
                          onChange={(e) => setAmountValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAmountSave(campaign.id);
                            } else if (e.key === 'Escape') {
                              handleAmountCancel();
                            }
                          }}
                          min="0"
                          step="0.01"
                          className="w-24 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleAmountSave(campaign.id)}
                            className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleAmountCancel}
                            className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative flex items-center justify-end">
                        <button
                          onClick={() => handleAmountEdit(campaign.id, campaign.amount || 0)}
                          className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                          title="Edit amount"
                        >
                          ✏️
                        </button>
                        <span className="numeric-cell">{formatCurrency(campaign.amount || 0)}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 tabular-nums w-28">
                    {editingViews === campaign.id ? (
                      <div className="flex flex-col space-y-2">
                        <input
                          type="number"
                          value={viewsValue}
                          onChange={(e) => setViewsValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleViewsSave(campaign.id);
                            } else if (e.key === 'Escape') {
                              handleViewsCancel();
                            }
                          }}
                          min="0"
                          className="w-24 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewsSave(campaign.id)}
                            className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleViewsCancel}
                            className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative flex items-center justify-end">
                        <button
                          onClick={() => handleViewsEdit(campaign.id, campaign.views || 0)}
                          className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                          title="Edit views"
                        >
                          ✏️
                        </button>
                        <span className="numeric-cell">{formatNumber(campaign.views || 0)}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 tabular-nums w-24">
                    {editingLikes === campaign.id ? (
                      <div className="flex flex-col space-y-2">
                        <input
                          type="number"
                          value={likesValue}
                          onChange={(e) => setLikesValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleLikesSave(campaign.id);
                            } else if (e.key === 'Escape') {
                              handleLikesCancel();
                            }
                          }}
                          min="0"
                          className="w-24 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleLikesSave(campaign.id)}
                            className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleLikesCancel}
                            className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative flex items-center justify-end">
                        <button
                          onClick={() => handleLikesEdit(campaign.id, campaign.likes || 0)}
                          className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                          title="Edit likes"
                        >
                          ✏️
                        </button>
                        <span className="numeric-cell">{formatNumber(campaign.likes || 0)}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 tabular-nums w-24">
                    {editingComments === campaign.id ? (
                      <div className="flex flex-col space-y-2">
                        <input
                          type="number"
                          value={commentsValue}
                          onChange={(e) => setCommentsValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleCommentsSave(campaign.id);
                            } else if (e.key === 'Escape') {
                              handleCommentsCancel();
                            }
                          }}
                          min="0"
                          className="w-24 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleCommentsSave(campaign.id)}
                            className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCommentsCancel}
                            className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative flex items-center justify-end">
                        <button
                          onClick={() => handleCommentsEdit(campaign.id, campaign.comments || 0)}
                          className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                          title="Edit comments"
                        >
                          ✏️
                        </button>
                        <span className="numeric-cell">{formatNumber(campaign.comments || 0)}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 w-20">
                    {editingShares === campaign.id && shareEditorField === 'reshares' ? (
                      <div className="flex flex-col space-y-2">
                        <input
                          type="number"
                          value={sharesValue}
                          onChange={(e) => setSharesValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSharesSave(campaign.id, 'reshares');
                            } else if (e.key === 'Escape') {
                              handleSharesCancel();
                            }
                          }}
                          min="0"
                          className="w-24 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleSharesSave(campaign.id, 'reshares')}
                            className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleSharesCancel}
                            className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative flex items-center justify-end">
                        <button
                          onClick={() => handleSharesEdit(campaign.id, campaign.reposts || campaign.shares || 0, 'reshares')}
                          className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                          title="Edit reshares"
                        >
                          ✏️
                        </button>
                        <span className="numeric-cell">{formatNumber(campaign.reposts || campaign.shares || 0)}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 w-20">
                    {editingShares === campaign.id && shareEditorField === 'shares' ? (
                      <div className="flex flex-col space-y-2">
                        <input
                          type="number"
                          value={sharesValue}
                          onChange={(e) => setSharesValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSharesSave(campaign.id, 'shares');
                            } else if (e.key === 'Escape') {
                              handleSharesCancel();
                            }
                          }}
                          min="0"
                          className="w-24 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleSharesSave(campaign.id, 'shares')}
                            className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleSharesCancel}
                            className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative flex items-center justify-end">
                        <button
                          onClick={() => handleSharesEdit(campaign.id, campaign.shares || 0, 'shares')}
                          className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                          title="Edit shares"
                        >
                          ✏️
                        </button>
                        <span className="numeric-cell">{formatNumber(campaign.shares || 0)}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-blue-600 w-16">
                    <span className="numeric-cell">{isNaN(campaign.tcs_score) ? '0' : Math.round(campaign.tcs_score || 0)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getVerdictColor(campaign.tcs_verdict)}`}>
                      {typeof campaign.tcs_verdict === 'string' ? campaign.tcs_verdict : 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-green-600">
                    {isNaN(campaign.er_percentage) ? '0.00%' : formatPercentage(campaign.er_percentage || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getVerdictColor(campaign.er_verdict)}`}>
                      {typeof campaign.er_verdict === 'string' ? campaign.er_verdict : 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-red-600">
                    {isNaN(campaign.cpv) ? '₹0' : formatCurrency(campaign.cpv || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getVerdictColor(campaign.cpv_verdict)}`}>
                      {typeof campaign.cpv_verdict === 'string' ? campaign.cpv_verdict : 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-purple-600">
                    {isNaN(campaign.cpe) ? '₹0' : formatCurrency(campaign.cpe || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getVerdictColor(campaign.cpe_verdict)}`}>
                      {typeof campaign.cpe_verdict === 'string' ? campaign.cpe_verdict : 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-orange-600">
                    {isNaN(campaign.cost_per_comment) ? '₹0' : formatCurrency(campaign.cost_per_comment || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getVerdictColor(campaign.comment_verdict)}`}>
                      {typeof campaign.comment_verdict === 'string' ? campaign.comment_verdict : 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-teal-600">
                    {isNaN(campaign.cost_per_share) ? '₹0' : formatCurrency(campaign.cost_per_share || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getVerdictColor(campaign.share_verdict)}`}>
                      {typeof campaign.share_verdict === 'string' ? campaign.share_verdict : 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-indigo-600">
                    {isNaN(campaign.cost_per_repost) ? '₹0' : formatCurrency(campaign.cost_per_repost || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getVerdictColor(campaign.repost_verdict)}`}>
                      {typeof campaign.repost_verdict === 'string' ? campaign.repost_verdict : 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {campaign.video_url ? (
                      <button
                        onClick={() => handleVideoClick(campaign.video_url)}
                        className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        title={`View video: ${campaign.video_url}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">No video</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex space-x-2 justify-center">
                      {/* Refresh Metrics Button */}
                      {campaign.post_link && (
                        <button
                          onClick={() => handleRefreshMetrics(campaign.id)}
                          disabled={refreshingCampaigns.has(campaign.id)}
                          className={`inline-flex items-center px-2 py-1.5 border border-transparent text-xs font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ${
                            refreshingCampaigns.has(campaign.id)
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700 hover:shadow-md'
                          }`}
                          title="Refresh engagement metrics from post URL"
                        >
                          {refreshingCampaigns.has(campaign.id) ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Refreshing...
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Refresh
                            </>
                          )}
                        </button>
                      )}
                      
                      {/* Delete Button */}
                      <button
                        onClick={() => handleDelete(campaign.id, campaign.influencer_name)}
                        disabled={deletingId === campaign.id}
                        className={`inline-flex items-center px-2 py-1.5 border border-transparent text-xs font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 ${
                          deletingId === campaign.id
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-red-600 hover:bg-red-700 hover:shadow-md'
                        }`}
                        title="Delete campaign"
                      >
                        {deletingId === campaign.id ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Deleting...
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Show more/less controls */}
      <div className="flex flex-col items-center justify-center gap-2 px-2 py-4 text-sm text-gray-600">
        <span>
          Showing {Math.min(visibleCount, filteredAndSortedCampaigns.length)} of {filteredAndSortedCampaigns.length} campaigns
        </span>
        <div className="flex items-center gap-2">
          {visibleCount < filteredAndSortedCampaigns.length && (
            <button
              onClick={handleShowMore}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Show more
            </button>
          )}
          {visibleCount > INITIAL_VISIBLE && (
            <button
              onClick={handleShowLess}
              className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-800 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            >
              Show less
            </button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Spend</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(campaigns.reduce((sum, c) => sum + (c.amount || 0), 0))}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Views</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatNumber(campaigns.reduce((sum, c) => sum + (c.views || 0), 0))}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Engagements</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatNumber(campaigns.reduce((sum, c) => sum + (c.likes || 0) + (c.comments || 0) + (c.shares || 0) + (c.reposts || 0), 0))}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Avg TCS</p>
              <p className="text-lg font-semibold text-gray-900">
                {Math.round(campaigns.reduce((sum, c) => sum + (c.tcs_score || 0), 0) / campaigns.length) || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Caption Modal */}
      {showCaptionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Post Caption</h3>
                <button
                  onClick={() => setShowCaptionModal(false)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 whitespace-pre-wrap max-h-96 overflow-y-auto">
                  {selectedCaption}
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={() => setShowCaptionModal(false)}
                  className="px-4 py-2 bg-blue-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    {/* Campaign Detail Modal */}
    {showDetailModal && (
      <CampaignDetailModal
        campaign={selectedCampaign}
        onClose={() => setShowDetailModal(false)}
      />
    )}

    {/* Video Modal */}
    {showVideoModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl overflow-hidden" style={{ width: '540px', height: '960px' }}>
          <div className="flex items-center justify-between p-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Video Player</h3>
            <button
              onClick={handleVideoModalClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-3 h-full flex flex-col">
            <div className="flex-1 flex items-center justify-center">
              <video
                className="w-full h-full rounded-lg object-cover"
                controls
                autoPlay
                src={selectedVideoUrl}
                onError={(e) => {
                  console.error('Video load error:', e);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              >
                Your browser does not support the video tag.
              </video>
              <div 
                className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg"
                style={{ display: 'none' }}
              >
                <div className="text-center">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-500 text-base">Video not available</p>
                  <p className="text-gray-400 text-xs mt-1">The video URL may be invalid or the video format is not supported.</p>
                  <button
                    onClick={() => window.open(selectedVideoUrl, '_blank')}
                    className="mt-3 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
                  >
                    Open in New Tab
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-3 text-xs text-gray-600">
              <p><strong>Video URL:</strong> <a href={selectedVideoUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{selectedVideoUrl}</a></p>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default CampaignsList;